import { Button, ModalTitle, ModalContent } from "@kaltura/ds-react-components";
import { Add24Icon } from "@kaltura/ds-react-icons";
import { postKmsData, translate, translatePlural } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import {
    SearchAndSelect,
    SearchAndSelectColumnId,
    SearchAndSelectSource,
} from "@kaltura/mediaspace-shared-search-and-select";
import { StyledActionButtonsContainer, StyledModal, StyledModalActions } from "./styledComponents";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { MediaType } from "../types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface AddToPlaylistProps {
    /** should button use primary or secondary color and which aria-label should be used */
    isEmptyPlaylistScreen?: boolean;
    /** Callback when media items are added and "Select" is clicked */
    onMediaAdded: (updatedMedia: MediaType[]) => void;
    /** Tabs configuration */
    tabs: SearchAndSelectSource[];
    /** get-media api url */
    getMediaApiUrl: string;
    /** Current media in the playlist */
    media: MediaType[];
    /** Number of items to show per page */
    pageSize: number;
    /** The ID of the playlist to which media is being added */
    playlistId: string;
}

export const AddToPlaylist = (props: AddToPlaylistProps) => {
    const { isEmptyPlaylistScreen = false, onMediaAdded, tabs, getMediaApiUrl, media, pageSize, playlistId } = props;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [processing, setProcessing] = useState(false);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleButtonClick = () => {
        setIsModalOpen(true);
        sendButtonAnalytics(
            `Add to playlist${media.length === 0 ? " (Empty state)" : ""}`,
            ButtonClickAnalyticsType.OPEN,
            playlistId,
            "Playlists",
            "Modal - Add Media to playlist"
        );
    };

    const handleCloseModal = () => {
        sendButtonAnalytics(
            "Add to playlist - Cancel",
            ButtonClickAnalyticsType.CLOSE,
            playlistId,
            "Playlists",
            "Modal - Add Media to playlist"
        );
        setIsModalOpen(false);
        setSelectedItems([]);
    };

    const { showToast } = useToastsContext();

    const handleAddContent = async () => {
        sendButtonAnalytics(
            "Add to playlist - Select",
            ButtonClickAnalyticsType.ADD,
            playlistId,
            "Playlists",
            "Modal - Add Media to playlist"
        );
        if (selectedItems.length > 0) {
            try {
                setProcessing(true);
                const result = await postKmsData(getMediaApiUrl, { mediaIds: selectedItems });
                if (result.success) {
                    onMediaAdded([...media, ...result.media]);
                }
                else {
                    showToast({ severity: "error", message: result.errorMessage });
                }
                setProcessing(false);
            }
            catch (error) {
                showToast({ severity: "error", message: translate("Error fetching selected media data") });
                setProcessing(false);
            }
        }
        handleCloseModal();
    };

    const handleSelectionChange = (selection: string[]) => {
        setSelectedItems(selection);
    };

    const baseClassName = "kms-ds-edit-playlist-add-to-playlist";

    return (
        <>
            <Button
                startIcon={<Add24Icon />}
                onClick={handleButtonClick}
                color={isEmptyPlaylistScreen ? "secondary" : "primary"}
                className={`${baseClassName}-button`}
                aria-label={isEmptyPlaylistScreen ? translate("Add to playlist") : translate("Add to empty playlist")}
            >
                {translate("Add to playlist")}
            </Button>

            <StyledModal open={isModalOpen} onClose={handleCloseModal} className={`${baseClassName}-modal`} fullWidth>
                <ModalTitle className={`${baseClassName}-modal-title`}>{translate("Select Media")}</ModalTitle>
                <ModalContent className={`${baseClassName}-modal-content`}>
                    <SearchAndSelect
                        tabs={tabs}
                        columns={[
                            SearchAndSelectColumnId.name,
                            SearchAndSelectColumnId.mediaType,
                            SearchAndSelectColumnId.createdBy,
                        ]}
                        columnsWidth={{
                            [SearchAndSelectColumnId.name]: "62%",
                            [SearchAndSelectColumnId.mediaType]: "160px",
                            [SearchAndSelectColumnId.createdBy]: "160px",
                        }}
                        selectionMode="multiple"
                        onSelectionChange={handleSelectionChange}
                        pageSize={pageSize}
                        searchBarAriaLabel={translate("Enter text to search for media")}
                    />
                </ModalContent>
                <StyledModalActions className={`${baseClassName}-modal-actions`}>
                    {translatePlural("%1 item selected", "%1 items selected", [selectedItems.length])}
                    <StyledActionButtonsContainer>
                        <Button
                            color="secondary"
                            onClick={handleCloseModal}
                            className={`${baseClassName}-modal-actions-cancel-button`}
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            onClick={handleAddContent}
                            loading={processing}
                            className={`${baseClassName}-modal-actions-select-button`}
                        >
                            {translate("Select")}
                        </Button>
                    </StyledActionButtonsContainer>
                </StyledModalActions>
            </StyledModal>
        </>
    );
};
